import { AddressItemType, Brand as BrandType, DeliveryZone, GQL_ID } from '@@types'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { GQL_QUERY_SEARCH_FOR_ADDRESS_SUGGESTIONS } from '@entities/Address/hooks'

import { useSlotStore } from '@widgets/slots'

import { client } from '@shared/api/client'
import { SHOP_ID } from '@shared/contants'

export type DeliveryType = 'pickup' | 'delivery' | 'express' | 'shop' | null;

// Реэкспорт типов для удобства
export type Brand = BrandType;
export interface GeoPoint {
  latitude?: number | null
  longitude?: number | null
}
export type { AvailableBrandInfo } from '@entities/DeliveryZone'

interface AddressState {
  selectedAddress: AddressItemType | null;
  savedAddresses: AddressItemType[];
  deliveryType: DeliveryType;
  shopId: GQL_ID;
  zoneId: GQL_ID | null;
  hasHydrated: boolean;
  isCorrectSearchAddress: boolean;
  brand: Brand | null;
  geoPoint: GeoPoint | null;
  
  // Поля для работы с адресом на карте
  currentAddress: string | null;
  loadingCheckAddressGeopoint: boolean;
  isChecking: boolean;
  
  // Setters
  setHasHydrated: (value: boolean) => void;
  setSelectedAddress: (address: AddressItemType) => void;
  setDeliveryType: (type: DeliveryType) => void;
  addAddress: (address: AddressItemType) => void;
  removeAddress: (id: GQL_ID) => void;
  clearSelectedAddress: () => void;
  isPickup: () => boolean;
  isDelivery: () => boolean;
  isActiveAddress: (id: GQL_ID | null | undefined) => boolean;
  setShopId: (shopId: GQL_ID) => void;
  setZoneId: (zoneId: GQL_ID | null) => void;
  setBrand: (brand: Brand | null) => void;
  setGeoPoint: (geoPoint: GeoPoint | null) => void;
  clearSavedAddresses: () => void;
  
  // Получение адреса по координатам (reverse geocoding)
  setCurrentAddress: (address: string | null) => void;
  fetchAddressForGeopoint: (
    geoPoint: GeoPoint,
    onHasAddress?: (address: string) => void,
  ) => Promise<void>;
  
  // Синхронизация с зонами
  updateFromZone: (zone: DeliveryZone) => void;
  selectBrand: (brand: Brand, zones: DeliveryZone[]) => void;
}

export const useAddressStore = create(
  persist<AddressState>(
    (set, get) => ({
      selectedAddress: null,
      savedAddresses: [],
      deliveryType: null,
      shopId: SHOP_ID,
      zoneId: null,
      hasHydrated: false,
      isCorrectSearchAddress: false,
      brand: null,
      geoPoint: null,
      
      // Поля для работы с адресом на карте
      currentAddress: null,
      loadingCheckAddressGeopoint: false,
      isChecking: false,

      setHasHydrated: (value) => set({ hasHydrated: value }),

      setDeliveryType: (type) => set({ deliveryType: type }),

      setSelectedAddress: (address) => {
        const shopId = address?.shop_id ? address.shop_id : SHOP_ID
        const geoPoint = address?.customerGeoPoint || null

        const clearSelectedSlot = useSlotStore.getState().clearSelectedSlot
        clearSelectedSlot()

        set({
          selectedAddress: address,
          shopId,
          geoPoint,
        })
      },

      setShopId: (shopId) => set({ shopId }),

      setZoneId: (zoneId) => set({ zoneId }),

      setBrand: (brand) => set({ brand }),

      setGeoPoint: (geoPoint) => set({ geoPoint }),
      
      // Получение адреса по координатам (reverse geocoding)
      setCurrentAddress: (address) => set({ currentAddress: address }),
      
      /**
       * Получает текстовый адрес по координатам (reverse geocoding)
       * Зоны загружаются отдельно через useDelivery → useDeliveryZonesForGeoPoint
       */
      fetchAddressForGeopoint: async (
        geoPoint: GeoPoint,
        onHasAddress = (_address) => {},
      ) => {
        if (get().isChecking) return
        if (!geoPoint.latitude || !geoPoint.longitude) return

        set({ isChecking: true, loadingCheckAddressGeopoint: true })

        try {
          const addressResponse = await client.query({
            query: GQL_QUERY_SEARCH_FOR_ADDRESS_SUGGESTIONS,
            variables: {
              filter: {
                latitude: geoPoint.latitude,
                longitude: geoPoint.longitude,
              },
            },
          })

          const address =
            addressResponse.data?.searchForAddressSuggestions?.suggestions?.[0]?.value

          if (address) {
            set({ currentAddress: address })
            onHasAddress(address)
          }
        }
        catch (error) {
          console.warn('fetchAddressForGeopoint error:', error)
          // Не показываем модалку — ошибка получения адреса не критична
        }
        finally {
          set({ isChecking: false, loadingCheckAddressGeopoint: false })
        }
      },

      addAddress: (address) => {
        const { savedAddresses } = get()
        set({
          savedAddresses: [ ...savedAddresses, address ],
        })
      },

      removeAddress: (id) => {
        const { savedAddresses, selectedAddress } = get()
        const updated = savedAddresses.filter((a) => a.id !== id)
        const isSelectedRemoved = selectedAddress?.id === id

        set({
          savedAddresses: updated,
          selectedAddress: isSelectedRemoved ? null : selectedAddress,
          deliveryType: isSelectedRemoved ? null : get().deliveryType,
          shopId: isSelectedRemoved ? SHOP_ID : get().shopId,
          zoneId: isSelectedRemoved ? null : get().zoneId,
          geoPoint: isSelectedRemoved ? null : get().geoPoint,
        })
      },

      clearSelectedAddress: () => {
        set({
          selectedAddress: null,
          deliveryType: null,
          shopId: SHOP_ID,
          zoneId: null,
          geoPoint: null,
        })
      },

      clearSavedAddresses: () => set({ savedAddresses: [] }),

      isPickup: () => get().deliveryType === 'pickup',

      isDelivery: () => get().deliveryType === 'delivery' || get().deliveryType === 'express',

      isActiveAddress: (id) => {
        const currentId = get().selectedAddress?.id
        return Boolean(id && currentId && id === currentId)
      },

      // Обновление состояния из зоны (синхронизация zone → shop)
      updateFromZone: (zone) => {
        set({
          zoneId: zone.id,
          shopId: zone.shop_id ? String(zone.shop_id) : undefined,
          brand: zone.shop?.brand || null,
        })
      },

      // Выбор бренда с автоматическим обновлением зоны
      selectBrand: (brand, zones) => {
        const zoneForBrand = zones.find(
          (z) => String(z.shop?.brand?.id) === String(brand.id)
        )
        
        if (zoneForBrand) {
          set({
            brand,
            zoneId: zoneForBrand.id,
            shopId: zoneForBrand.shop_id ? String(zoneForBrand.shop_id) : undefined,
          })
        }
        else {
          set({ brand })
        }
      },
    }),
    {
      name: 'address-store',
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    },
  ),
)
